import React from "react";
import useEmblaCarousel, { type UseEmblaCarouselType } from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type CarouselApi = UseEmblaCarouselType[1];

type CarouselProps = {
	opts?: Parameters<typeof useEmblaCarousel>[0];
	orientation?: "horizontal" | "vertical";
	setApi?: (api: CarouselApi) => void;
	className?: string;
	children: React.ReactNode;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: CarouselApi | undefined;
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
	orientation: "horizontal" | "vertical";
};

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

const useCarousel = () => {
	const context = React.useContext(CarouselContext);
	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel />");
	}
	return context;
};

export function Carousel({
	orientation = "horizontal",
	opts,
	setApi,
	className,
	children,
}: CarouselProps) {
	const [carouselRef, api] = useEmblaCarousel({
		...opts,
		axis: orientation === "horizontal" ? "x" : "y",
	});
	const [canScrollPrev, setCanScrollPrev] = React.useState(false);
	const [canScrollNext, setCanScrollNext] = React.useState(false);

	const onSelect = React.useCallback((emblaApi: CarouselApi) => {
		if (!emblaApi) {
			return;
		}
		setCanScrollPrev(emblaApi.canScrollPrev());
		setCanScrollNext(emblaApi.canScrollNext());
	}, []);

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	React.useEffect(() => {
		if (!api || !setApi) {
			return;
		}
		setApi(api);
	}, [api, setApi]);

	React.useEffect(() => {
		if (!api) {
			return;
		}
		onSelect(api);
		api.on("reInit", onSelect);
		api.on("select", onSelect);
		return () => {
			api.off("select", onSelect);
			api.off("reInit", onSelect);
		};
	}, [api, onSelect]);

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api,
				orientation,
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
			}}
		>
			<div className={cn("relative", className)} role="region" aria-roledescription="carousel">
				{children}
			</div>
		</CarouselContext.Provider>
	);
}

export function CarouselContent({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	const { carouselRef, orientation } = useCarousel();

	return (
		<div ref={carouselRef} className="overflow-hidden">
			<div
				className={cn(
					"flex",
					orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
					className,
				)}
				{...props}
			/>
		</div>
	);
}

export function CarouselItem({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
	const { orientation } = useCarousel();

	return (
		<div
			role="group"
			aria-roledescription="slide"
			className={cn(
				"min-w-0 shrink-0 grow-0 basis-full",
				orientation === "horizontal" ? "pl-4" : "pt-4",
				className,
			)}
			{...props}
		/>
	);
}

export function CarouselPrevious({ className, ...props }: React.ComponentProps<typeof Button>) {
	const { orientation, scrollPrev, canScrollPrev } = useCarousel();

	return (
		<Button
			variant="outline"
			size="icon"
			className={cn(
				"h-8 w-8 rounded-md p-0",
				orientation === "horizontal"
					? "absolute left-0 top-1/2 -translate-y-1/2"
					: "absolute left-1/2 top-0 -translate-x-1/2 rotate-90",
				className,
			)}
			disabled={!canScrollPrev}
			onClick={scrollPrev}
			{...props}
		>
			<ChevronLeft className="h-4 w-4" />
			<span className="sr-only">Previous slide</span>
		</Button>
	);
}

export function CarouselNext({ className, ...props }: React.ComponentProps<typeof Button>) {
	const { orientation, scrollNext, canScrollNext } = useCarousel();

	return (
		<Button
			variant="outline"
			size="icon"
			className={cn(
				"h-8 w-8 rounded-md p-0",
				orientation === "horizontal"
					? "absolute right-0 top-1/2 -translate-y-1/2"
					: "absolute bottom-0 left-1/2 -translate-x-1/2 rotate-90",
				className,
			)}
			disabled={!canScrollNext}
			onClick={scrollNext}
			{...props}
		>
			<ChevronRight className="h-4 w-4" />
			<span className="sr-only">Next slide</span>
		</Button>
	);
}

